// src/components/dashboard/Dashboard.js
import React, { useState, useEffect } from 'react';
import {
  CreditCardIcon,
  DocumentArrowUpIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PlusIcon,
  ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { t } from '../../translations';
import { useTransactions, TRANSACTION_STATUS } from '../../hooks/useTransactions';
import { useUserCredits } from '../../hooks/useUserCredits';
import FileUpload from '../upload/FileUpload';
import TransactionHistory from './TransactionHistory';
import CreditPurchaseModal from '../payment/CreditPurchaseModal';
import AnonymousConversionModal from '../AnonymousConversionModal';
import RefreshBanner from '../common/RefreshBanner';
import { useNavigate, Link } from 'react-router-dom';
import {
  trackDashboardView,
  trackCreditPurchaseView,
  trackUserEngagement,
  trackError
} from '../../utils/analytics';

const Dashboard = () => {
  const {
    currentUser,
    logout,
    // Anonymous session support
    anonymousUser,
    anonymousCredits,
    isAnonymous
  } = useAuth();

  // Real-time credit updates via Firestore listener
  const {
    credits: userCredits,
    loading: creditsLoading,
    error: creditsError
  } = useUserCredits();
  const { language } = useLocale();
  const navigate = useNavigate();
  const {
    getTransactionsByStatus
  } = useTransactions();

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  // Anonymous conversion modal state
  const [showAnonymousModal, setShowAnonymousModal] = useState(false);

  // Redirect if neither authenticated nor anonymous user
  useEffect(() => {
    if (!currentUser && !anonymousUser) {
      console.log('⚠️ Dashboard: No user found, redirecting to home');
      navigate('/');
    }
  }, [currentUser, anonymousUser, navigate]);

  // Note: Modal trigger removed - was showing too early (right after accepting preview)
  // Modal now only shows when user tries to upload with 0 credits (handled by FileUpload component)
  // This gives users time to download their CSV before being prompted to register

  // Track dashboard view on component mount
  useEffect(() => {
    if (currentUser) {
      trackDashboardView({
        user_id: currentUser.uid,
        credits_available: userCredits || 0,
        session_start: new Date().toISOString(),
        user_type: 'registered'
      });
    } else if (anonymousUser) {
      trackDashboardView({
        user_id: 'anonymous',
        credits_available: anonymousCredits || 0,
        session_start: new Date().toISOString(),
        user_type: 'anonymous'
      });
    }
  }, [currentUser, anonymousUser, userCredits, anonymousCredits]);

  // Calculate stats
  const completedCount = getTransactionsByStatus(TRANSACTION_STATUS.COMPLETED).length;
  const processingCount = getTransactionsByStatus(TRANSACTION_STATUS.PROCESSING).length;
  const errorCount = getTransactionsByStatus(TRANSACTION_STATUS.ERROR).length;

  // ← Added logout handler
  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      
      // Track user logout
      trackUserEngagement('logout', {
        user_id: currentUser?.uid,
        session_duration: 'session_end'
      });
      
      await logout();
      navigate('/'); // Redirect to home page
    } catch (error) {
      console.error('Error during logout:', error);
      
      // Track logout error
      trackError({
        errorType: 'logout_error',
        errorMessage: error.message || 'Failed to logout',
        component: 'Dashboard'
      });
      
      alert('Failed to logout. Please try again.');
    } finally {
      setLogoutLoading(false);
    }
  };

  // Handle successful payment (both Paystack and Paddle)
  const handlePaymentSuccess = async (paymentResponse) => {
    setPaymentLoading(true);
    try {
      // Detect payment provider
      const provider = paymentResponse.provider || 'paystack';

      // Extract credits based on provider
      const credits = provider === 'paddle'
        ? paymentResponse.credits || 0
        : paymentResponse.metadata?.credits || 0;

      console.log(`Processing ${provider} payment:`, paymentResponse);

      // Show success message immediately - real-time listener will update credits automatically
      alert(
        `✅ Payment successful!\n\n` +
        `Your ${credits} credits will appear within 30 seconds.\n` +
        `The page will update automatically - no need to refresh!`
      );

      // No webhook waiting needed! The useUserCredits hook with real-time
      // Firestore listener automatically updates credits when webhook processes

    } catch (error) {
      console.error('Error processing payment:', error);
      alert(error.message);
    } finally {
      setPaymentLoading(false);
      setShowPaymentModal(false);  // Close modal immediately
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="main-container">
      {/* Header with Anonymous Support */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {t(language, 'dashboard.title')}
                </h1>
                <p className="text-gray-600">
                  {isAnonymous
                    ? `🎯 ${t(language, 'dashboard.trialMode')}`
                    : t(language, 'dashboard.trialDescription')}
                </p>
              </div>

              {/* Right section with credits and user info */}
              <div className="flex items-center space-x-6">
                {/* Credits Display */}
                <div className="text-right">
                  <div className="text-sm text-gray-500">
                    {isAnonymous ? t(language, 'dashboard.freeTrialCredits') : t(language, 'dashboard.creditsAvailable')}
                  </div>
                  <div className={`text-2xl font-bold ${isAnonymous ? 'text-orange-600' : 'text-green-600'}`} data-testid="credit-balance">
                    {isAnonymous ? (anonymousCredits || 0) : (userCredits || 0)}
                  </div>
                </div>

                {/* Conditional Buttons based on user type */}
                {isAnonymous ? (
                  // Anonymous user: Show "Register to Get 10 Free Credits" button
                  <Link to="/register">
                    <button
                      className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      {t(language, 'dashboard.registerFreeCredits')}
                    </button>
                  </Link>
                ) : (
                  // Registered user: Show "Buy Credits" button
                  <button
                    onClick={() => {
                      trackCreditPurchaseView({
                        user_id: currentUser?.uid,
                        current_credits: userCredits || 0,
                        source: 'dashboard_header'
                      });
                      setShowPaymentModal(true);
                    }}
                    disabled={paymentLoading}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center"
                    data-testid="payment-modal-trigger"
                  >
                    {paymentLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t(language, 'dashboard.processing')}...
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4 mr-2" />
                        {t(language, 'dashboard.buyCredits')}
                      </>
                    )}
                  </button>
                )}

                {/* User Info & Logout Section */}
                <div className="flex items-center space-x-4 pl-4 border-l border-gray-200">
                  {/* User Info */}
                  <div className="hidden sm:block text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {isAnonymous ? 'Guest User' : (currentUser?.displayName || 'User')}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isAnonymous ? 'Free Trial' : currentUser?.email}
                    </p>
                  </div>

                  {/* Logout/Register Button */}
                  {!isAnonymous && (
                    <button
                      onClick={handleLogout}
                      disabled={logoutLoading}
                      className={`
                        flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors
                        ${logoutLoading
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                      title={t(language, 'dashboard.logout')}
                    >
                      <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">
                        {logoutLoading ? `${t(language, 'dashboard.signingOut')}...` : t(language, 'dashboard.logout')}
                      </span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Banner for New Registered Users (not anonymous) */}
        {!isAnonymous && (
          <RefreshBanner
            userCredits={userCredits}
            currentUser={currentUser}
          />
        )}

        {/* Session Warning for Anonymous Users */}
        {isAnonymous && (
          <div className="mb-6 bg-orange-50 border border-orange-300 rounded-lg p-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-6 w-6 text-orange-600 mr-3 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-orange-900">
                  ⚠️ Important: Temporary Session
                </h3>
                <p className="text-sm text-orange-800 mt-2 leading-relaxed">
                  You are using a free trial session. <strong>If you close this browser or navigate away, your session will end and you will lose access to your converted files.</strong>
                </p>
                <p className="text-sm text-orange-800 mt-2">
                  Register now to keep your files accessible for 30 days and get 10 free credits!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Upload Section */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-4">
                {t(language, 'dashboard.uploadNew')}
              </h2>
              <div data-testid="upload-area">
                <FileUpload />
              </div>
            </div>
          </div>

          {/* Transaction History Section */}
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                {t(language, 'dashboard.recentConversions')}
              </h2>
              <p className="text-sm text-gray-500 mb-4">
                {t(language, 'dashboard.privacyNote')}
              </p>
              <div data-testid="transaction-history">
                <TransactionHistory limit={5} />
              </div>
            </div>
          </div>
        </div>

        {/* Credit Purchase Modal */}
        <CreditPurchaseModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSuccess={handlePaymentSuccess}
        />

        {/* Anonymous Conversion Complete Modal */}
        <AnonymousConversionModal
          isOpen={showAnonymousModal}
          onClose={() => setShowAnonymousModal(false)}
        />
      </div>
    </div>
  );
};

export default Dashboard;